"use client";

import { CardFull as CardFullComponent } from "@repo/ui-parkmobile/CardFull";
import type { CardFull } from "@/generated/sb";
import {
  imageStoryblokResolver,
  assetResolver,
  sectionTitleResolver,
  videoResolver,
} from "@/lib/storyblok/adaptors";
import { storyblokEditable } from "@storyblok/react/rsc";
import { extractVideoConfig } from "@/lib/utils/wistia";
import { useWistiaId } from "@/hooks/useWistiaId";
import Video from "@/components/Video";
import {
  getSectionName,
  injectButtonAnalytics,
  getContentDescription,
} from "@/utils/analytics";
import { trackFAQClick } from "@/utils/trackingScripts";

interface CardFullProps {
  blok: CardFull;
  section?: string;
}

export default function CardFull({ blok, section }: CardFullProps) {
  const imageBlock = blok.image?.[0];

  // Check if it's a video component
  const isVideo = imageBlock?.component === "video";

  const image = blok.image?.[0];
  const resolvedImage =
    imageBlock && imageBlock.component === "image"
      ? imageStoryblokResolver(imageBlock)
      : undefined;

  const resolvedVideo =
    isVideo && imageBlock ? videoResolver(imageBlock) : undefined;

  const cardBackgroundImage = blok.cardBackgroundImage?.[0];
  const resolvedCardBackgroundImage = cardBackgroundImage
    ? imageStoryblokResolver(cardBackgroundImage) || undefined
    : undefined;

  const sectionBackgroundImage = blok.sectionBackgroundImage?.[0];
  const resolvedSectionBackgroundImage = sectionBackgroundImage
    ? imageStoryblokResolver(sectionBackgroundImage) || undefined
    : undefined;

  const sectionName = getSectionName(blok.typography, section);
  const contentDescription = getContentDescription(blok.typography);

  let sectionTitle = blok.typography
    ? sectionTitleResolver(blok.typography)
    : undefined;

  if (sectionTitle) {
    const buttonsWithAnalytics = injectButtonAnalytics(
      sectionTitle.buttons,
      sectionName,
      contentDescription,
    );

    const typographyWithAnalytics = sectionTitle.typography?.map((item) => {
      if (item.type === "accordion") {
        const accordionItem = item as any;
        return {
          ...accordionItem,
          type: "accordion" as const,
          onItemToggle: (index: number, title: string, isOpen: boolean) => {
            if (isOpen) {
              trackFAQClick({
                section: sectionName,
                innerText: title,
                config: "accordionItem",
              });
            }
          },
        };
      }

      if (item.type === "list" && "items" in item) {
        const hasAccordionItems = item.items?.some(
          (listItem: any) => listItem.type === "accordionItem",
        );

        if (hasAccordionItems) {
          const listItem = item as any;
          return {
            ...listItem,
            type: "list" as const,
            onItemToggle: (index: number, title: string, isOpen: boolean) => {
              if (isOpen) {
                trackFAQClick({
                  section: sectionName,
                  innerText: title,
                  config: "accordionItem",
                });
              }
            },
          };
        }
      }

      return item;
    });

    sectionTitle = {
      ...sectionTitle,
      buttons: buttonsWithAnalytics,
      typography: typographyWithAnalytics,
    };
  }

  const videoButton = blok.videoPlayerButton?.[0];

  const playButtonImageAsset = videoButton?.playImage
    ? assetResolver(videoButton.playImage)
    : undefined;

  const playButtonImage = playButtonImageAsset
    ? {
        src: playButtonImageAsset.src,
        alt: playButtonImageAsset.alt,
        width: playButtonImageAsset.width || 100,
        height: playButtonImageAsset.height || 100,
      }
    : undefined;

  const videoId = useWistiaId(videoButton?.videoLink);

  const videoPlayerConfig = extractVideoConfig(videoButton?.configuration);

  // If it's a video component, create a custom video element
  const videoComponent =
    isVideo && imageBlock && resolvedVideo ? (
      <Video
        blok={imageBlock}
        aspectRatio={resolvedVideo.aspectRatio}
        quality={resolvedVideo.quality}
        borderRadius="var(--radius-lg)"
        imageSpace="fill"
        playButtonImage={playButtonImage}
      />
    ) : undefined;

  return (
    <CardFullComponent
      {...storyblokEditable(blok)}
      image={resolvedImage}
      video={videoComponent}
      cardBackgroundImage={resolvedCardBackgroundImage}
      sectionBackgroundImage={resolvedSectionBackgroundImage}
      orientation={blok.orientation}
      typographyPosition={blok.typographyPosition}
      imageAlignment={blok.imageAlignment}
      imageHorizontalAlignment={blok.imageHorizontalAlignment}
      sectionTitle={sectionTitle || undefined}
      cardBackgroundColor={blok.cardBackgroundColor || "primary"}
      backgroundColor={blok.backgroundColor || "none"}
      topPadding={blok.topPadding || "none"}
      bottomPadding={blok.bottomPadding || "none"}
      id={blok.anchor}
      videoId={videoId}
      showVideoButton={!!videoButton && !!videoId}
      playButtonImage={playButtonImage}
      videoConfig={videoPlayerConfig || undefined}
      disableBottomPaddingOnMobile={blok.disableBottomPaddingOnMobile}
    />
  );
}
